import { useEffect, useState } from "react";
import type { Editor as TipTapEditor } from "@tiptap/core";
import { useApi } from '../../providers/api-provider';
import { watchNetworkImagePastes } from './network-images';
import { standaloneClipboardImage } from './pasted-images';
import { insideFieldContainer } from './field-container';

/** Embed clipboard image files directly in the fragment HTML as data URLs. */
export function usePasteFiles(editor: TipTapEditor | null) {
  const api = useApi();
  const [status, setStatus] = useState<string | null>(null);
  useEffect(() => {
    setStatus(null);
    if (!editor || !api) return;
    let failureMessage = '';
    return watchNetworkImagePastes(editor, (url, signal) => api.importImage(url, signal), (pending, failed) => {
      if (!failed) failureMessage = '';
      setStatus(pending ? `Embedding ${pending} pasted image${pending === 1 ? '' : 's'}… Please wait before saving or changing fragments.`
        : failed ? `Some pasted images could not be embedded. Their original links were kept.${failureMessage ? ' ' + failureMessage : ''}` : null);
    }, message => { failureMessage = message; });
  }, [editor, api]);
  useEffect(() => {
    if (!editor) return;
    let active = true;

    const insertImage = (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (!active || editor.isDestroyed || typeof reader.result !== "string") return;
        editor.chain().focus().insertContent({
          type: "image",
          attrs: { src: reader.result, alt: file.name || null },
        }).run();
      };
      reader.onerror = () => console.error("Failed to read pasted image", reader.error);
      reader.readAsDataURL(file);
    };

    const onPaste = (event: ClipboardEvent) => {
      // Containers have their own paste validation. Let field-only clipboard HTML
      // reach ProseMirror, while keeping this file/image importer out of containers.
      if (insideFieldContainer(editor)) return;
      const clipboard = event.clipboardData;
      if (!clipboard) return;
      const html = clipboard.getData('text/html').trim();
      const standalone = html ? standaloneClipboardImage(html) : null;
      // Preserve text/image order in rich fragments, even if a preview file is offered.
      if (html && !standalone) return;
      const images: File[] = [];

      for (const item of Array.from(clipboard.items || [])) {
        if (item.kind !== "file") continue;
        const file = item.getAsFile();
        if (file?.type.startsWith("image/")) images.push(file);
      }
      // Some browsers expose files without clipboard items. Avoid handling both twice.
      if (images.length === 0) {
        images.push(...Array.from(clipboard.files || []).filter(file => file.type.startsWith("image/")));
      }
      if (images.length === 0) {
        if (!standalone) return;
        event.preventDefault();
        // Use the normal paste transaction so network embedding and Undo still apply.
        editor.view.pasteHTML(`<figure class="image-block">${standalone.outerHTML}</figure>`, event);
        return;
      }

      event.preventDefault();
      images.forEach(insertImage);
    };

    const editorNode = editor.view.dom;
    // Classify before ProseMirror consumes an HTML-only image paste as inline content.
    editorNode.addEventListener("paste", onPaste, true);
    return () => {
      active = false;
      editorNode.removeEventListener("paste", onPaste, true);
    };
  }, [editor]);
  return status;
}
